import {
  Box,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { IoReload } from 'react-icons/io5'
import { MdDelete } from 'react-icons/md'
import { currencyMask } from '../../utils/masks'

const formatarData = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d)) return value
  const dia = String(d.getDate()).padStart(2, '0')
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const ano = d.getFullYear()
  return `${dia}/${mes}/${ano}`
}

const formatarDataHora = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d)) return value
  const dia = String(d.getDate()).padStart(2, '0')
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const ano = d.getFullYear()
  const hora = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const seg = String(d.getSeconds()).padStart(2, '0')
  return `${dia}/${mes}/${ano} ${hora}:${min}:${seg}`
}

const PX = 1

const Cabecalho = ({
  colunas,
  estiloColunas,
  estiloCabecalho,
  modoSelecao,
  aoExcluir,
  aoSelecionarTodos,
  todosSelecionados,
}) => (
  <Grid
    container
    alignItems="center"
    flexWrap="nowrap"
    sx={{
      px: PX,
      py: 1,
      bgcolor: 'grey.100',
      borderBottom: 1,
      borderColor: 'divider',
      flexShrink: 0,
      width: '100%',
    }}
  >
    {modoSelecao === 'multiple' && (
      <Box sx={{ width: 42, flexShrink: 0, textAlign: 'center' }}>
        <Checkbox
          size="small"
          checked={todosSelecionados}
          indeterminate={!todosSelecionados && modoSelecao === 'multiple'}
          onChange={aoSelecionarTodos}
          sx={{ p: 0 }}
        />
      </Box>
    )}
    {colunas.map((col, i) => (
      <Grid
        item
        key={i}
        sx={{
          width: estiloColunas?.[i]?.width,
          minWidth: estiloColunas?.[i]?.minWidth || estiloColunas?.[i]?.width,
          textAlign: estiloColunas?.[i]?.textAlign || 'left',
          ...estiloCabecalho?.[i],
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, color: 'grey.600', fontSize: 13 }}
        >
          {col}
        </Typography>
      </Grid>
    ))}
    {aoExcluir && <Box sx={{ width: 44, flexShrink: 0 }} />}
  </Grid>
)

const Corpo = ({
  dados,
  colunas,
  estiloColunas,
  estiloLinhas,
  opcoesColuna,
  colunasData,
  colunasDataHora,
  colunasMoeda,
  aoSelecionarLinha,
  aoExcluir,
  modoSelecao,
  selecao,
  aoClicarLinha,
  aoAlterarSelecao,
  alturaScroll,
  semBordas,
  refMedicao,
  maxLinhas,
  alturaMinimaLinha,
  preencherCorpo,
  compactarLinhas,
}) => {
  const formatarCelula = (value, idx) => {
    if (colunasMoeda.includes(idx)) {
      const num = Number(value)
      if (isNaN(num)) return value
      return `R$ ${currencyMask(num)}`
    }
    if (colunasData.includes(idx)) return formatarData(value)
    if (colunasDataHora.includes(idx)) return formatarDataHora(value)
    return value
  }

  return (
    <Box
      sx={{
        overflowY: 'auto',
        overflowX: 'hidden',
        height: preencherCorpo ? '100%' : undefined,
        maxHeight: preencherCorpo ? undefined : alturaScroll,
        minHeight: 0,
      }}
    >
      {dados.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            py: 3,
            color: 'grey.500',
          }}
        >
          Nenhum registro encontrado
        </Typography>
      ) : (
        dados.map((row, i) => {
          const estaSelecionado =
            modoSelecao === 'multiple'
              ? selecao?.includes(row.original)
              : selecao === row.original

          return (
            <Grid
              container
              alignItems="center"
              flexWrap="nowrap"
              key={i}
              ref={i === 0 ? refMedicao : undefined}
              onClick={() => aoClicarLinha?.(row)}
              sx={{
                px: PX,
                py: compactarLinhas ? 0.25 : 1.25,
                ...(semBordas
                  ? {}
                  : { borderBottom: 1, borderColor: 'divider' }),
                cursor: aoSelecionarLinha ? 'pointer' : undefined,
                bgcolor: estaSelecionado ? 'action.selected' : undefined,
                '&:hover': { bgcolor: 'action.hover' },
                minHeight: alturaMinimaLinha ?? 38 * maxLinhas,
                width: '100%',
              }}
            >
              {modoSelecao === 'multiple' && (
                <Box sx={{ width: 42, flexShrink: 0, textAlign: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={estaSelecionado}
                    onChange={() => aoAlterarSelecao?.(row)}
                    sx={{ p: 0 }}
                  />
                </Box>
              )}
              {colunas.map((_, j) => {
                const rawValue = row.formatado?.[j]
                const formatted =
                  rawValue !== undefined ? formatarCelula(rawValue, j) : ''
                const opt = opcoesColuna?.find((o) => o.alias === j)
                const content =
                  opt && row.original
                    ? opt.customJsx({
                        index: i,
                        itemFormatado: formatted,
                        linhaFormatada: row.formatado,
                        linhaOriginal: row.original,
                      })
                    : formatted

                const truncar = estiloColunas?.[j]?.truncate
                const celulaSx = estiloLinhas?.[j]

                return (
                  <Grid
                    item
                    key={j}
                    sx={{
                      width: estiloColunas?.[j]?.width,
                      minWidth:
                        estiloColunas?.[j]?.minWidth ||
                        estiloColunas?.[j]?.width,
                      textAlign: estiloColunas?.[j]?.textAlign || 'left',
                      ...celulaSx,
                      paddingRight: 1,
                    }}
                  >
                    {truncar ? (
                      <Tooltip
                        title={
                          typeof content === 'string' ||
                          typeof content === 'number'
                            ? String(content)
                            : null
                        }
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {content}
                        </Typography>
                      </Tooltip>
                    ) : typeof content === 'string' ||
                      typeof content === 'number' ? (
                      <Typography variant="body2">{content}</Typography>
                    ) : (
                      content
                    )}
                  </Grid>
                )
              })}
              {aoExcluir && (
                <Box
                  sx={{
                    width: 44,
                    flexShrink: 0,
                    textAlign: 'center',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const deletado = row.formatado?.[aoExcluir.index]
                    return deletado ? (
                      <IoReload
                        style={{
                          cursor: 'pointer',
                          display: 'inline-block',
                        }}
                        onClick={() => aoExcluir.action(row.original)}
                      />
                    ) : (
                      <MdDelete
                        style={{
                          cursor: 'pointer',
                          display: 'inline-block',
                        }}
                        onClick={() => aoExcluir.action(row.original)}
                      />
                    )
                  })()}
                </Box>
              )}
            </Grid>
          )
        })
      )}
    </Box>
  )
}

const LinhaTotal = ({
  rotuloTotal,
  colunasTotal,
  valorTotal,
  totais,
  semBordas,
  sx,
}) => {
  const autoTotal = colunasTotal.length > 0 && totais?.length > 0
  const customTotal = valorTotal != null
  const temTotal = autoTotal || customTotal
  if (!temTotal) return null

  const totalExibir = customTotal ? valorTotal : totais[0]

  // const ultimaCol = colunas.length - 1

  return (
    <Grid
      container
      alignItems="center"
      flexWrap="nowrap"
      sx={{
        px: PX,
        display: 'flex',
        py: 1.25,
        ...(semBordas ? {} : { borderTop: 1, borderColor: 'divider' }),
        bgcolor: 'grey.50',
        flexShrink: 1,
        width: '100%',
        ...sx,
      }}
    >
      <Grid item xs={4}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            width: 'fit-content',
          }}
        >
          {rotuloTotal}
        </Typography>
      </Grid>
      <Grid item xs={8} container justifyContent={'end'}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, width: 'fit-content' }}
        >
          R$ {currencyMask(totalExibir || 0)}
        </Typography>
      </Grid>
    </Grid>
  )
}

const CardTable = ({
  titulo,
  dadosTabela = [],
  mapear,
  colunas = [],
  colunasData,
  colunasDataHora,
  colunasMoeda,
  estiloColunas,
  estiloCabecalho,
  estiloLinhas,
  opcoesColuna,
  colunasTotal,
  rotuloTotal = 'Total',
  valorTotal,
  conteudoTopo,
  acaoTitulo,
  maxLinhas,
  alturaMinimaLinha,
  preencherCorpo,
  compactarLinhas,
  alturaMaxima,
  aoSelecionarLinha,
  aoExcluir,
  modoSelecao,
  selecao,
  aoAlterarSelecao,
  semBordas,
  preencherAltura,
  sx,
}) => {
  const cData = colunasData || []
  const cDataHora = colunasDataHora || []
  const cMoeda = colunasMoeda || []
  const estCol = estiloColunas || []
  const estCab = estiloCabecalho || []
  const estLin = estiloLinhas || []
  const opCol = opcoesColuna || []

  const dados = useMemo(() => {
    return (dadosTabela || []).map((item) => ({
      original: item,
      formatado: mapear ? mapear(item) : Object.values(item),
    }))
  }, [dadosTabela, mapear])

  const totais = useMemo(() => {
    const cols = colunasTotal || []
    return cols.map((colIdx) => {
      return dados.reduce((acc, row) => {
        const val = Number(row.formatado[colIdx]) || 0
        return acc + val
      }, 0)
    })
  }, [colunasTotal, dados])

  const larguraTotal = useMemo(() => {
    const somaCol = (estiloColunas || []).reduce((acc, col) => {
      if (typeof col?.width === 'number') return acc + col.width
      if (typeof col?.width === 'string' && col.width.includes('px'))
        return acc + parseInt(col.width, 10)
      return acc
    }, 0)
    return somaCol > 0 ? somaCol + PX * 16 : 0
  }, [estiloColunas])

  const [alturaLinha, setAlturaLinha] = useState(38)

  const refMedicao = useCallback((node) => {
    if (node) {
      const h = node.offsetHeight
      if (h > 0) setAlturaLinha(h)
    }
  }, [])

  const alturaScroll = useMemo(() => {
    if (alturaMaxima) return alturaMaxima
    if (maxLinhas && dados.length > maxLinhas)
      return maxLinhas * alturaLinha + 2
    return undefined
  }, [alturaMaxima, maxLinhas, dados.length, alturaLinha])

  const aoClicarLinha = (row) => {
    if (modoSelecao === 'multiple') {
      tratarAlterarSelecao(row)
    }
    aoSelecionarLinha?.(row.original)
  }

  const tratarAlterarSelecao = (row) => {
    if (modoSelecao === 'multiple') {
      const atual = selecao || []
      const idx = atual.indexOf(row.original)
      const proximo =
        idx >= 0 ? atual.filter((_, i) => i !== idx) : [...atual, row.original]
      aoAlterarSelecao?.({ value: proximo })
    } else {
      aoAlterarSelecao?.(selecao === row.original ? null : row.original)
    }
  }

  const aoMarcarTodos = () => {
    if (!aoAlterarSelecao) return
    const todos = selecao?.length === dados.length
    aoAlterarSelecao?.({
      value: todos ? [] : dados.map((r) => r.original),
    })
  }

  const todosSelecionados =
    modoSelecao === 'multiple' &&
    dados.length > 0 &&
    selecao?.length === dados.length

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: preencherAltura ? '100%' : undefined,
        width: preencherAltura ? '100%' : undefined,
        ...sx,
      }}
    >
      {titulo && (
        <Box
          sx={{
            px: PX,
            pt: 1.5,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
            {titulo}
          </Typography>
          {acaoTitulo}
        </Box>
      )}
      {conteudoTopo && (
        <>
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>{conteudoTopo}</Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          flex: preencherAltura ? 1 : undefined,
          minHeight: preencherAltura ? 0 : undefined,
        }}
      >
        <Box
          sx={{
            minWidth: larguraTotal ? `${larguraTotal}px` : '100%',
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateRows: 'auto 1fr auto',
            flex: preencherAltura ? 1 : undefined,
            minHeight: preencherAltura ? 0 : undefined,
          }}
        >
          <Cabecalho
            colunas={colunas}
            estiloColunas={estCol}
            estiloCabecalho={estCab}
            modoSelecao={modoSelecao}
            aoExcluir={aoExcluir}
            aoSelecionarTodos={aoMarcarTodos}
            todosSelecionados={todosSelecionados}
          />

          <Corpo
            dados={dados}
            colunas={colunas}
            estiloColunas={estCol}
            estiloLinhas={estLin}
            maxLinhas={maxLinhas}
            alturaMinimaLinha={alturaMinimaLinha}
            preencherCorpo={preencherCorpo}
            compactarLinhas={compactarLinhas}
            opcoesColuna={opCol}
            colunasData={cData}
            colunasDataHora={cDataHora}
            colunasMoeda={cMoeda}
            aoSelecionarLinha={aoSelecionarLinha}
            aoExcluir={aoExcluir}
            modoSelecao={modoSelecao}
            selecao={selecao}
            aoClicarLinha={aoClicarLinha}
            aoAlterarSelecao={tratarAlterarSelecao}
            alturaScroll={alturaScroll}
            semBordas={semBordas}
            refMedicao={refMedicao}
          />

          <LinhaTotal
            colunas={colunas}
            estiloColunas={estCol}
            totais={totais}
            rotuloTotal={rotuloTotal}
            colunasTotal={colunasTotal || []}
            valorTotal={valorTotal}
            semBordas={semBordas}
          />
        </Box>
      </Box>
    </Paper>
  )
}

export default CardTable
